import React from "react";
import IconPlaneNe from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconPlaneNe.js";
import IconArrowRight from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowRight.js";
import styled, { css } from "styled-components";
import media from "@/styles/mediaQueries";
import { format, differenceInCalendarDays } from "date-fns";
import { de } from "date-fns/locale";
import { Flight, Flights } from "@/lib/Types/Flight";
import { Text } from "@lmn/design-system";
import AirlineIcon from "@/components/UI/AirlineIcon/AirlineIcon";

const TitleContainer = styled.div`
  margin-bottom: 1rem;
`;

const TopLine = styled.div<{ $isOutward?: boolean }>`
  display: flex;
  width: 100%;
  gap: 0.5rem;
  line-height: 1;
  align-items: center;
  margin-bottom: 0.3rem;
  ${({ $isOutward }) =>
    !$isOutward && media.screenSmMin`margin-bottom: 0.3rem;`};
  data {
    display: block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    line-height: 1;
  }
`;

const BottomLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  gap: 0.2rem;
  line-height: 1;
  color: ${(props) => props.theme.kallisto.color.text.static.textSecondary};
  data {
    white-space: nowrap;
  }
`;
const Dates = styled.div`
  display: flex;
  gap: 0.2rem;
  align-items: center;
`;

const DateInfo = styled.div`
  margin-left: auto;
  display: flex;
  gap: 0.2rem;
  align-items: center;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) =>
    theme.kallisto.color.border.static.borderNeutral};
  margin: 0.8rem 0;
`;

const FlightIcon = styled(IconPlaneNe) <{ $return?: boolean }>`
  width: 1rem;
  height: 1rem;
  transform: rotate(45deg);
  ${(props) =>
    props.$return &&
    css`
      transform: rotate(225deg);
    `}
`;

const ArrivalTimeWrapper = styled.span<{ $daysDiff?: number }>`
  position: relative;

  ${({ $daysDiff }) =>
    $daysDiff &&
    $daysDiff > 0 &&
    css`
      &::after {
        content: "+${$daysDiff}";
        position: absolute;
        top: -6px;
        right: -12px;
        font-size: 0.6em;
      }
    `}
`;

const FlightContainer = styled.div<{ $hasChanged?: boolean }>`
  ${({ $hasChanged, theme }) =>
    $hasChanged &&
    css`
      padding: ${theme.kallisto.space.space100};
      border-radius: ${theme.kallisto.border.radius.radius100};
      border: 1px solid ${theme.kallisto.color.border.static.borderWarning};
      background-color: ${theme.kallisto.color.background.static
        .backgroundWarningSubtle};
    `}
`;

const FlightChangedLabel = styled(Text)`
  margin-bottom: ${({ theme }) => theme.kallisto.space.space50};
`;

const formats = {
  dateShort: "eee dd.MM.",
  timeShort: "HH:mm",
};

function parseDurationMinutes(duration: string | number): number {
  if (typeof duration === "number") return duration;
  if (!duration) return 0;
  // ISO 8601 duration (e.g., "PT6H30M" from QuickVacancy)
  const isoMatch = duration.match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
  if (isoMatch) {
    const hours = parseInt(isoMatch[1] || "0", 10);
    const minutes = parseInt(isoMatch[2] || "0", 10);
    return hours * 60 + minutes;
  }
  // Plain minutes (e.g., "1615" from datelist)
  return parseInt(duration, 10) || 0;
}

function getStopsLabel(flights: Flight[]): string {
  const firstFlight = flights[0];

  // If API provides explicit directFlight/numberOfStops, use those
  if (firstFlight && "directFlight" in firstFlight) {
    const directFlight = firstFlight.directFlight;
    const numberOfStops = firstFlight.numberOfStops ?? 0;

    if (directFlight) return "Direktflug";
    // Inconsistent data: not direct but 0 stops — show nothing
    if (!directFlight && numberOfStops === 0) return "";
    return numberOfStops === 1 ? "1 Stopp" : `${numberOfStops} Stopps`;
  }

  // Fallback: use array length (multi-segment flights)
  if (flights.length === 1) return "Direktflug";
  return flights.length === 2 ? "1 Stopp" : `${flights.length - 1} Stopps`;
}

interface FlightOverviewProps {
  flights: Flight[];
  isOutward: boolean;
  showAirlineIcon: boolean;
  hasChanged?: boolean;
}

const FlightOverview: React.FC<FlightOverviewProps> = ({
  flights,
  isOutward,
  showAirlineIcon,
  hasChanged,
}) => {
  const departure = flights[0].departure;
  const destination = flights[flights.length - 1].destination;

  const departureTime = new Date(departure.datetime);
  const destinationTime = new Date(destination.datetime);
  const daysDifference = differenceInCalendarDays(
    destinationTime,
    departureTime
  );

  // Sum flight durations + layover durations to get total journey time
  const totalDurationMinutes = flights.reduce((total, flight) => {
    const flightMin = parseDurationMinutes(flight.duration);
    const layoverMin = flight.stopover && typeof flight.stopover === "object" && "duration" in flight.stopover
      ? parseDurationMinutes(flight.stopover.duration)
      : 0;
    return total + flightMin + layoverMin;
  }, 0);
  const hours = Math.floor(totalDurationMinutes / 60);
  const minutes = totalDurationMinutes % 60;
  const formattedDuration = `${hours}:${minutes
    .toString()
    .padStart(2, "0")} Std`;

  return (
    <FlightContainer $hasChanged={hasChanged}>
      {hasChanged && (
        <FlightChangedLabel
          variant="BodySmall"
          weight="medium"
          color="textWarning"
        >
          FLUGÄNDERUNG
        </FlightChangedLabel>
      )}
      <TopLine $isOutward={isOutward}>
        {isOutward ? <FlightIcon /> : <FlightIcon $return={true} />}
        <Dates>
          <time dateTime={departure.datetime}>
            <Text forceTag="time" weight="medium">
              {format(departureTime, formats.timeShort, { locale: de })}
            </Text>
          </time>
          <span>-</span>
          <time dateTime={destination.datetime}>
            <Text forceTag="time" weight="medium">
              <ArrivalTimeWrapper $daysDiff={daysDifference}>
                {format(destinationTime, formats.timeShort, { locale: de })}
              </ArrivalTimeWrapper>
            </Text>
          </time>
        </Dates>
        <DateInfo>
          {getStopsLabel(flights) && (
            <Text variant="BodySmall" data-testid="stops" color="textSecondary">
              {getStopsLabel(flights)}
            </Text>
          )}
          {totalDurationMinutes > 0 && (
            <>
              {getStopsLabel(flights) && <Text variant="BodySmall">·</Text>}
              <Text variant="BodySmall" forceTag="time" color="textSecondary">
                {formattedDuration}
              </Text>
            </>
          )}
        </DateInfo>
      </TopLine>

      <BottomLine>
        {showAirlineIcon && flights[0]?.airlineCode && (
          <AirlineIcon
            airlineCode={flights[0].airlineCode}
            airlineName={flights[0].airlineName}
          />
        )}
        <Text variant="BodySmall" forceTag="data" color="textSecondary">
          {departure.locality}
        </Text>
        <IconArrowRight size={10} />
        <Text variant="BodySmall" forceTag="data" color="textSecondary">
          {destination.locality}
        </Text>
      </BottomLine>
    </FlightContainer>
  );
};

interface Props {
  flights: Flights;
  showAirlineIcon?: boolean;
  outwardFlightChanged?: boolean;
  returnFlightChanged?: boolean;
}

const FlightDetails: React.FC<Props> = ({
  flights,
  showAirlineIcon = false,
  outwardFlightChanged,
  returnFlightChanged,
}) => {
  const outwardDeparture = flights.outwardFlight[0]?.departure.datetime;
  const returnDeparture = flights.returnFlight[0]?.departure.datetime;

  return (
    <>
      <TitleContainer>
        <Text variant="Title5">
          {outwardDeparture &&
            format(new Date(outwardDeparture), formats.dateShort, {
              locale: de,
            })}
          {outwardDeparture && returnDeparture && " – "}
          {returnDeparture &&
            format(new Date(returnDeparture), formats.dateShort, {
              locale: de,
            })}
        </Text>
      </TitleContainer>

      <FlightOverview
        flights={flights.outwardFlight}
        isOutward={true}
        showAirlineIcon={showAirlineIcon}
        hasChanged={outwardFlightChanged}
      />
      <Separator />
      <FlightOverview
        flights={flights.returnFlight}
        isOutward={false}
        showAirlineIcon={showAirlineIcon}
        hasChanged={returnFlightChanged}
      />
    </>
  );
};

export default FlightDetails;
